import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { ROLE_LABELS, ROLE_COLORS } from '../../config/constants';
import ChatAttachment from './ChatAttachment';

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMins / 60);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;

  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const renderContent = (text, isOwn) => {
  if (!text) return null;
  const parts = text.split(/(@\w[\w\s]*?\w(?=\s|$)|@\w+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return (
        <Typography
          key={i}
          component="span"
          sx={{
            fontWeight: 700,
            backgroundColor: isOwn ? 'rgba(255,255,255,0.25)' : 'rgba(26,115,232,0.12)',
            color: isOwn ? '#fff' : '#1A73E8',
            borderRadius: '4px',
            px: 0.5,
            py: 0.1,
            fontSize: 'inherit',
            lineHeight: 'inherit',
          }}
        >
          {part}
        </Typography>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const ChatMessage = ({ message, isOwn }) => {
  const { senderName, senderRole, content, createdAt, attachments } = message;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        mb: 0.75,
        px: 1.5,
        '@keyframes slideIn': {
          from: {
            opacity: 0,
            transform: isOwn ? 'translateX(12px)' : 'translateX(-12px)',
          },
          to: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        animation: 'slideIn 0.2s ease-out',
      }}
    >
      <Box
        sx={{
          maxWidth: '75%',
          minWidth: 100,
        }}
      >
        {/* Sender info */}
        {!isOwn && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.7rem', color: '#202124' }}>
              {senderName}
            </Typography>
            {senderRole && (
              <Chip
                label={ROLE_LABELS[senderRole] || senderRole}
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.58rem',
                  fontWeight: 600,
                  backgroundColor: ROLE_COLORS[senderRole] || '#94A3B8',
                  color: '#fff',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Box>
        )}

        {/* Message bubble */}
        <Box
          sx={{
            backgroundColor: isOwn ? '#1A73E8' : '#FFFFFF',
            color: isOwn ? '#fff' : '#202124',
            borderRadius: '12px',
            borderTopRightRadius: isOwn ? '4px' : '12px',
            borderTopLeftRadius: isOwn ? '12px' : '4px',
            px: 1.5,
            py: 0.75,
            wordBreak: 'break-word',
            border: isOwn ? 'none' : '1px solid #E2E8F0',
            boxShadow: isOwn
              ? '0 1px 3px rgba(26, 115, 232, 0.2)'
              : '0 1px 2px rgba(15, 23, 42, 0.04)',
          }}
        >
          {content && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.45 }}>
              {renderContent(content, isOwn)}
            </Typography>
          )}

          {/* Attachments */}
          {attachments && attachments.length > 0 && (
            <Box sx={{ mt: content ? 0.5 : 0 }}>
              {attachments.map((att, idx) => (
                <ChatAttachment key={idx} attachment={att} />
              ))}
            </Box>
          )}
        </Box>

        {/* Timestamp */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: isOwn ? 'right' : 'left',
            mt: 0.25,
            px: 0.5,
            fontSize: '0.65rem',
            color: '#94A3B8',
          }}
        >
          {formatTime(createdAt)}
        </Typography>
      </Box>
    </Box>
  );
};

export default ChatMessage;
